import { motion, useReducedMotion } from "framer-motion";

/* ------------------------------------------------------------------ *
 * Shared rust route + waypoint device — the ONE "route to the summit"
 * line used everywhere the site references its own terrain metaphor
 * structurally (About's elevation marker, Process's four stages,
 * FinalCTA's echo). Same catmull-rom spline math as
 * tools/render_availability.py's route sketch, ported to SVG.
 * Control points are fractions (0-1) of the given viewBox.
 * ------------------------------------------------------------------ */

function catmullRom(pts, steps = 24) {
  const out = [];
  const ext = [pts[0], ...pts, pts[pts.length - 1]];
  for (let i = 1; i < ext.length - 2; i++) {
    const [p0, p1, p2, p3] = [ext[i - 1], ext[i], ext[i + 1], ext[i + 2]];
    for (let s = 0; s < steps; s++) {
      const t = s / steps;
      const t2 = t * t, t3 = t2 * t;
      const x =
        0.5 *
        (2 * p1[0] + (-p0[0] + p2[0]) * t + (2 * p0[0] - 5 * p1[0] + 4 * p2[0] - p3[0]) * t2 +
          (-p0[0] + 3 * p1[0] - 3 * p2[0] + p3[0]) * t3);
      const y =
        0.5 *
        (2 * p1[1] + (-p0[1] + p2[1]) * t + (2 * p0[1] - 5 * p1[1] + 4 * p2[1] - p3[1]) * t2 +
          (-p0[1] + 3 * p1[1] - 3 * p2[1] + p3[1]) * t3);
      out.push([x, y]);
    }
  }
  out.push(pts[pts.length - 1]);
  return out;
}

/**
 * Same spline as the SVG route line itself, sampled as percentages
 * (0-100) of the viewBox rather than absolute px — for CSS-positioned
 * overlays (e.g. RouteHiker) that need to walk the exact line a
 * RouteWaypoints instance draws, without duplicating its curve math.
 */
export function getRoutePathPct(points, steps = 24) {
  return catmullRom(points, steps).map(([x, y]) => ({ xPct: x * 100, yPct: y * 100 }));
}

/**
 * @param {number[][]} points - control points as [xFrac, yFrac] (0-1)
 * @param {{at: number, label?: string, lit?: boolean}[]} waypoints - indices into `points`
 * @param {string} viewBox - e.g. "0 0 400 200"
 * @param {boolean} animate - draw the line in on scroll-into-view (once)
 */
export default function RouteWaypoints({
  points,
  waypoints = [],
  viewBox = "0 0 400 200",
  animate = true,
  className = "",
  labelColor = "#A13F2C",
}) {
  const reduce = useReducedMotion();
  const doAnimate = animate && !reduce;
  const [, , vbW, vbH] = viewBox.split(" ").map(Number);

  const abs = points.map(([x, y]) => [x * vbW, y * vbH]);
  const curve = catmullRom(abs);
  const d = "M" + curve.map(([x, y]) => `${x.toFixed(1)},${y.toFixed(1)}`).join(" L");

  return (
    <svg
      viewBox={viewBox}
      preserveAspectRatio="none"
      className={`w-full h-auto overflow-visible ${className}`}
      role="img"
      aria-label="Route line marking the site's build process"
    >
      <motion.path
        d={d}
        fill="none"
        stroke="#A13F2C"
        strokeWidth={2}
        strokeLinecap="round"
        vectorEffect="non-scaling-stroke"
        initial={doAnimate ? { opacity: 0 } : false}
        whileInView={doAnimate ? { opacity: 1 } : {}}
        viewport={{ once: true }}
        transition={{ duration: 1.1, ease: [0.16, 1, 0.3, 1] }}
      />
      {waypoints.map((wp, i) => {
        const [x, y] = abs[wp.at];
        const lit = wp.lit !== false;
        return (
          <g key={i} opacity={lit ? 1 : 0.35}>
            <circle cx={x} cy={y} r={5} fill={labelColor} />
            <circle cx={x} cy={y} r={9} fill="none" stroke={labelColor} strokeOpacity={0.4} strokeWidth={1} />
            {wp.label && (
              <text
                x={x}
                y={wp.labelAbove ? y - 14 : y + 20}
                textAnchor="middle"
                style={{
                  font: "600 11px 'IBM Plex Mono', monospace",
                  letterSpacing: "1px",
                  fill: labelColor,
                }}
              >
                {wp.label}
              </text>
            )}
          </g>
        );
      })}
    </svg>
  );
}
